﻿(() => {
  const desktopQuery = window.matchMedia('(min-width: 990px)');

  class HeaderNewMegaMenu extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.details = this.querySelector('.new-header__mega-details');
      this.trigger = this.querySelector('.new-header__mega-trigger');
      this.header = this.closest('.new-header');
      this.secondaryLinks = [...this.querySelectorAll('[data-new-mega-secondary]')];
      this.panels = [...this.querySelectorAll('[data-new-mega-panel]')];
      this.closeTimer = null;

      if (!this.details || !this.trigger) return;

      this.addEventListener('mouseenter', () => this.open());
      this.addEventListener('mouseleave', () => this.scheduleClose());
      this.addEventListener('focusin', () => this.open());
      this.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!this.contains(document.activeElement)) this.close();
        }, 0);
      });
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        this.close();
        this.trigger.focus();
      });
      this.details.addEventListener('toggle', () => this.syncState());
      this.secondaryLinks.forEach((link) => {
        link.addEventListener('mouseenter', () => this.activate(link.dataset.newMegaSecondary));
        link.addEventListener('focus', () => this.activate(link.dataset.newMegaSecondary));
      });
    }

    open() {
      if (!desktopQuery.matches) return;
      window.clearTimeout(this.closeTimer);
      document.querySelectorAll('header-new-mega-menu .new-header__mega-details[open]').forEach((details) => {
        if (details !== this.details) details.removeAttribute('open');
      });
      this.details.setAttribute('open', '');
      this.syncState();
    }

    scheduleClose() {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(() => this.close(), 160);
    }

    close() {
      window.clearTimeout(this.closeTimer);
      this.details.removeAttribute('open');
      this.syncState();
    }

    activate(key) {
      this.secondaryLinks.forEach((link) => link.classList.toggle('is-active', link.dataset.newMegaSecondary === key));
      this.panels.forEach((panel) => {
        const active = panel.dataset.newMegaPanel === key;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });
    }

    syncState() {
      const open = this.details.hasAttribute('open');
      this.trigger.setAttribute('aria-expanded', String(open));
      if (!this.header) return;
      const anyOpen = Boolean(this.header.querySelector('.new-header__mega-details[open]'));
      this.header.classList.toggle('new-header--mega-open', anyOpen);
    }
  }

  if (!customElements.get('header-new-mega-menu')) {
    customElements.define('header-new-mega-menu', HeaderNewMegaMenu);
  }

  const initializeHeaderState = (root = document) => {
    root.querySelectorAll('.new-header').forEach((header) => {
      if (header.dataset.headerStateReady === 'true') return;
      header.dataset.headerStateReady = 'true';

      const mobileOpen = header.querySelector('[data-new-mobile-open]');
      const mobileClose = header.querySelector('[data-new-mobile-close]');
      const mobileDrawer = header.querySelector('[data-new-mobile-drawer]');
      const mobileBackdrop = header.querySelector('[data-new-mobile-backdrop]');
      let drawerHideTimer = null;

      const resetMobilePanels = () => {
        if (!mobileDrawer) return;
        mobileDrawer.querySelectorAll('[data-new-mobile-accordion]').forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
        mobileDrawer.querySelectorAll('.new-header-mobile__menu, .new-header-mobile__submenu').forEach((panel) => panel.classList.remove('has-child-open', 'is-active'));
        mobileDrawer.querySelectorAll('.new-header-mobile__submenu').forEach((panel) => { panel.hidden = true; });
      };

      const closeMobileDrawer = (restoreFocus = false) => {
        if (!mobileDrawer || !mobileBackdrop || !mobileOpen) return;
        window.clearTimeout(drawerHideTimer);
        mobileDrawer.classList.remove('is-open');
        mobileBackdrop.classList.remove('is-open');
        mobileDrawer.setAttribute('aria-hidden', 'true');
        mobileOpen.setAttribute('aria-expanded', 'false');
        header.classList.remove('new-header--drawer-open');
        document.documentElement.classList.remove('new-header-mobile-drawer-open');
        drawerHideTimer = window.setTimeout(() => {
          if (!mobileBackdrop.classList.contains('is-open')) {
            mobileBackdrop.hidden = true;
            resetMobilePanels();
          }
        }, 230);
        if (restoreFocus) mobileOpen.focus();
      };

      const openMobileDrawer = () => {
        if (desktopQuery.matches || !mobileDrawer || !mobileBackdrop || !mobileOpen) return;
        window.clearTimeout(drawerHideTimer);
        mobileBackdrop.hidden = false;
        window.requestAnimationFrame(() => {
          mobileDrawer.classList.add('is-open');
          mobileBackdrop.classList.add('is-open');
        });
        mobileDrawer.setAttribute('aria-hidden', 'false');
        mobileOpen.setAttribute('aria-expanded', 'true');
        header.classList.add('new-header--drawer-open');
        document.documentElement.classList.add('new-header-mobile-drawer-open');
        mobileClose?.focus();
      };

      mobileOpen?.addEventListener('click', openMobileDrawer);
      mobileClose?.addEventListener('click', () => closeMobileDrawer(true));
      mobileBackdrop?.addEventListener('click', () => closeMobileDrawer(true));
      mobileDrawer?.querySelectorAll('[data-new-mobile-accordion]').forEach((toggle) => {
        toggle.addEventListener('click', () => {
          const panel = document.getElementById(toggle.getAttribute('aria-controls'));
          if (!panel) return;
          const parentPanel = toggle.closest('.new-header-mobile__submenu');
          const parentLayer = parentPanel || mobileDrawer.querySelector('.new-header-mobile__menu');
          toggle.setAttribute('aria-expanded', 'true');
          panel.hidden = false;
          window.requestAnimationFrame(() => {
            parentLayer?.classList.add('has-child-open');
            panel.classList.add('is-active');
          });
        });
      });
      mobileDrawer?.querySelectorAll('[data-new-mobile-back]').forEach((back) => {
        back.addEventListener('click', () => {
          const panel = back.closest('.new-header-mobile__submenu');
          if (!panel) return;
          const parentPanel = panel.parentElement?.closest('.new-header-mobile__submenu');
          const parentLayer = parentPanel || mobileDrawer.querySelector('.new-header-mobile__menu');
          const toggle = mobileDrawer.querySelector(`[aria-controls="${CSS.escape(panel.id)}"]`);
          toggle?.setAttribute('aria-expanded', 'false');
          panel.classList.remove('is-active');
          parentLayer?.classList.remove('has-child-open');
          window.setTimeout(() => {
            if (!panel.classList.contains('is-active')) panel.hidden = true;
          }, 280);
        });
      });      mobileDrawer?.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeMobileDrawer(true);
      });

      const navigation = header.querySelector('.new-header__navigation');
      if (!navigation) return;

      const items = [...navigation.querySelectorAll('.new-header__menu > .new-header__menu-item')];
      let pointerItem = null;
      let focusItem = null;

      const itemLink = (item) => item?.querySelector('.new-header__menu-link');
      const currentItem = items.find((item) => item.querySelector('[aria-current="page"]')) || null;

      const updateNavigationState = () => {
        const interactiveItem = focusItem || pointerItem;
        const highlightedItem = interactiveItem || currentItem;
        header.classList.toggle('new-header--nav-active', Boolean(interactiveItem));
        items.forEach((item) => itemLink(item)?.classList.toggle('is-active', item === highlightedItem));
      };

      const updateStickyState = () => {
        if (!desktopQuery.matches) {
          header.classList.toggle('is-sticky', window.scrollY > 8);
          header.classList.remove('new-header--nav-active');
          return;
        }
        header.classList.toggle('is-sticky', window.scrollY > 8);
        updateNavigationState();
      };

      items.forEach((item) => {
        item.addEventListener('mouseenter', () => {
          if (!desktopQuery.matches) return;
          pointerItem = item;
          updateNavigationState();
        });
        item.addEventListener('mouseleave', () => {
          pointerItem = null;
          updateNavigationState();
        });
        item.addEventListener('focusin', () => {
          if (!desktopQuery.matches) return;
          focusItem = item;
          updateNavigationState();
        });
        item.addEventListener('focusout', () => {
          window.setTimeout(() => {
            if (!item.contains(document.activeElement)) {
              focusItem = null;
              updateNavigationState();
            }
          }, 0);
        });
      });

      updateStickyState();
      window.addEventListener('scroll', updateStickyState, { passive: true });
      desktopQuery.addEventListener('change', () => {
        if (desktopQuery.matches) closeMobileDrawer(false);
        updateStickyState();
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeHeaderState(), { once: true });
  } else {
    initializeHeaderState();
  }

  document.addEventListener('shopify:section:load', (event) => initializeHeaderState(event.target));
})();

